/**
 * Zustand sync store — single source of truth for connectivity, sync progress,
 * queue depth, and offline-id reconciliation.
 *
 * NEW — offlineResolutions map (2026-06-04):
 *   When the SyncWorker drains a WAL batch, the backend returns
 *   SyncCommandResultDto.clientEntityId = the offline temp-id the client
 *   submitted (e.g. "offline-guest-1780485078502-2329") alongside the real
 *   server UUID in entityId.
 *
 *   desktopBridge.js writes these into offlineResolutions so that:
 *     1. desktopApiFetch.desktopGet can transparently redirect a stale offline-id
 *        lookup to the real UUID after reconciliation.
 *     2. Any component that stored an offline id in local state can call
 *        resolveId(offlineId) to get the current real UUID without re-mounting.
 *     3. App.jsx listens to the "hotelmis:offline:resolved" event and updates
 *        selectedId when the user is currently viewing a detail page for an
 *        entity whose id just got reconciled.
 */
import { create } from 'zustand';

export const useSyncStore = create((set, get) => ({
  // ── Connectivity ──────────────────────────────────────────────────────────
  online:          true,
  connectionState: 'UNKNOWN',

  // ── Queue ─────────────────────────────────────────────────────────────────
  pendingQueueCount: 0,
  failedQueueCount:  0,
  conflictCount:     0,

  // ── Sync progress ─────────────────────────────────────────────────────────
  syncing:           false,
  lastSyncAt:        null,
  phase:             'idle',
  bootstrapProgress: 0,

  // ── Offline-id reconciliation map ─────────────────────────────────────────
  // Key:   client-side offline temp-id  (e.g. "offline-guest-1780485078502-2329")
  // Value: { realId: string, entityType: string, resolvedAt: number }
  offlineResolutions: {},

  // ── Actions ───────────────────────────────────────────────────────────────
  updateFromStatus: (s) => set({
    online:            s.connection === 'ONLINE',
    connectionState:   s.connection    ?? 'UNKNOWN',
    pendingQueueCount: s.pending       ?? 0,
    failedQueueCount:  s.failed        ?? 0,
    conflictCount:     s.conflicts     ?? 0,
    syncing:           s.syncing       ?? false,
    lastSyncAt:        s.lastSync      ?? null,
    phase:             s.phase         ?? 'idle',
    bootstrapProgress: s.bootstrapProgress ?? 0,
  }),

  /**
   * Record that an offline temp-id was resolved to a real server UUID.
   * Called by desktopBridge.js when onCommandResult fires with clientEntityId.
   */
  addResolution: (offlineId, realId, entityType) => {
    if (!offlineId || !realId || offlineId === realId) return;
    set(state => ({
      offlineResolutions: {
        ...state.offlineResolutions,
        [offlineId]: { realId, entityType, resolvedAt: Date.now() },
      },
    }));
  },

  /**
   * Resolve an id: if it is a known offline temp-id, return the real UUID.
   * Otherwise return it unchanged.
   * Safe to call with null — returns null.
   */
  resolveId: (id) => {
    if (!id || !id.startsWith('offline-')) return id;
    const entry = get().offlineResolutions[id];
    return entry ? entry.realId : id;
  },
}));
